/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import ROUTES from 'modules/common/constants/route';
import { isAllowForRoles } from 'modules/common/helpers/token';
import {
  ADMIN_ROLE,
  PLATFORM_ADMIN_ROLE,
  PREFERRED_ADMIN_ROLE,
  SUPER_ADMIN_ROLE,
} from 'modules/common/constants/roles';
import {
  Article,
  GroupAddRounded,
  BarChart,
  FilterAlt,
  ManageAccounts,
  EventNote,
  LineWeight,
  Hotel,
  Apartment,
  Paid,
  SystemUpdate,
  AdminPanelSettings,
  Analytics,
} from '@mui/icons-material';
import { ENVIRONMENT } from '../../../../config';

/**
 * Sidebar nav items are defined here
 */
const getDashboardItems = (accessToken) => {
  const pagesSection = [];
  if (isAllowForRoles(accessToken, [SUPER_ADMIN_ROLE, ADMIN_ROLE])) {
    pagesSection.push(
      {
        href: ROUTES.DASHBOARD,
        icon: BarChart,
        title: 'Visuals',
      },
      {
        href: ROUTES.VISUAL_FILTER,
        icon: FilterAlt,
        title: 'Visual Filters',
      }
    );
  }

  if (
    isAllowForRoles(accessToken, [SUPER_ADMIN_ROLE, ADMIN_ROLE]) &&
    ENVIRONMENT.ENABLE_SCENARIO_TAB === 'true'
  ) {
    pagesSection.push({
      href: ROUTES.SCENARIO,
      icon: Article,
      title: 'Scenarios',
    });
  }

  if (isAllowForRoles(accessToken, [SUPER_ADMIN_ROLE, ADMIN_ROLE, PREFERRED_ADMIN_ROLE])) {
    pagesSection.push({
      href: ROUTES.USERS,
      icon: ManageAccounts,
      title: 'Users',
    });
  }

  if (isAllowForRoles(accessToken, [SUPER_ADMIN_ROLE, ADMIN_ROLE, PREFERRED_ADMIN_ROLE])) {
    pagesSection.push({
      href: ROUTES.USER_INVITATION,
      icon: GroupAddRounded,
      title: 'User Invitations',
    });
  }

  if (isAllowForRoles(accessToken, [SUPER_ADMIN_ROLE, ADMIN_ROLE])) {
    pagesSection.push({
      href: ROUTES.HOTELS,
      icon: Hotel,
      title: 'Hotels',
    });
  }

  if (isAllowForRoles(accessToken, [SUPER_ADMIN_ROLE, ADMIN_ROLE, PREFERRED_ADMIN_ROLE])) {
    pagesSection.push({
      href: ROUTES.TRANSACTIONS,
      icon: Paid,
      title: 'Transactions',
    });
  }

  if (isAllowForRoles(accessToken, [SUPER_ADMIN_ROLE, ADMIN_ROLE, PREFERRED_ADMIN_ROLE])) {
    pagesSection.push({
      href: ROUTES.HOTEL_SET,
      icon: Apartment,
      title: 'Hotel Sets',
    });
  }

  if (isAllowForRoles(accessToken, [SUPER_ADMIN_ROLE, ADMIN_ROLE])) {
    pagesSection.push({
      href: ROUTES.EVENT_CONFIGURATION,
      icon: EventNote,
      title: 'Event Configuration',
    });
  }

  if (isAllowForRoles(accessToken, [SUPER_ADMIN_ROLE])) {
    pagesSection.push({
      href: ROUTES.AUDIT_LOGS,
      icon: LineWeight,
      title: 'Audit Logs',
    });
  }

  if (isAllowForRoles(accessToken, [SUPER_ADMIN_ROLE, ADMIN_ROLE, PREFERRED_ADMIN_ROLE])) {
    pagesSection.push({
      href: ROUTES.SYSTEM,
      icon: SystemUpdate,
      title: 'System',
    });
  }
  if (isAllowForRoles(accessToken, [SUPER_ADMIN_ROLE, ADMIN_ROLE, PREFERRED_ADMIN_ROLE])) {
    pagesSection.push({
      href: ROUTES.LLM_STATISTICS,
      icon: Analytics,
      title: 'LLM Statistics',
    });
  }

  if (isAllowForRoles(accessToken, [PLATFORM_ADMIN_ROLE])) {
    pagesSection.push({
      href: ROUTES.PLATFORM_ADMIN_SETTINGS,
      icon: AdminPanelSettings,
      title: 'Platform Admin Settings',
    });
  }

  return [
    {
      title: 'Dashboard',
      pages: pagesSection,
    },
  ];
};

export default getDashboardItems;
